import api from './api';

export const adminService = {
  // ===== DASHBOARD & STATISTICS =====
  
  /**
   * Get dashboard statistics
   */
  async getDashboardStats() {
    const response = await api.get('/admin/dashboard/stats');
    return response.data;
  },
// Add to your admin service file

getBurnerMessages: () => {
  return api.get('/admin/burner/messages');
},

getBurnerMessageHistory: () => {
  return api.get('/admin/burner/messages/history');
},

sendBurnerMessage: (message, expires_minutes = 60) => {
  return api.post('/admin/burner/messages', { message, expires_minutes });
},

deleteBurnerMessage: (messageId) => {
  return api.delete(`/admin/burner/messages/${messageId}`);
},
  /**
   * Get system health status
   */
  async getSystemHealth() {
    const response = await api.get('/admin/system/health');
    return response.data;
  },

  // ===== USER MANAGEMENT =====
  
  /**
   * Get all users with filters
   * @param {Object} params - Query params (role, status, search, limit, offset)
   */
  async getUsers(params = {}) {
    const response = await api.get('/admin/users', { params });
    return response.data;
  },

  /**
   * Get detailed user information
   * @param {number} userId - User ID
   */
  async getUserDetails(userId) {
    const response = await api.get(`/admin/users/${userId}`);
    return response.data;
  },

  /**
   * Verify a user's email/account
   * @param {number} userId - User ID
   */
  async verifyUser(userId) {
    const response = await api.post(`/admin/users/${userId}/verify`);
    return response.data;
  },

  /**
   * Suspend a user account
   * @param {number} userId - User ID
   * @param {string} reason - Suspension reason
   */
  async suspendUser(userId, reason = 'No reason provided') {
    const response = await api.post(`/admin/users/${userId}/suspend`, { reason });
    return response.data;
  },

  /**
   * Activate a suspended user account
   * @param {number} userId - User ID
   */
  async activateUser(userId) {
    const response = await api.post(`/admin/users/${userId}/activate`);
    return response.data;
  },

  /**
   * Delete a user account (admin only)
   * @param {number} userId - User ID
   */
  async deleteUser(userId) {
    const response = await api.delete(`/admin/users/${userId}/delete`);
    return response.data;
  },

  // ===== AUCTION MANAGEMENT =====
  
  /**
   * Get pending auctions awaiting approval
   * @param {Object} params - Query params
   */
  async getPendingAuctions(params = {}) {
    const response = await api.get('/admin/auctions/pending', { params });
    return response.data;
  },

  /**
   * Get all auctions with advanced filters (admin)
   * @param {Object} params - Filters (status, date_range, etc.)
   */
  async getAllAuctions(params = {}) {
    const response = await api.get('/admin/auctions', { params });
    return response.data;
  },

  /**
   * Approve a pending auction
   * @param {number} auctionId - Auction ID
   */
  async approveAuction(auctionId) {
    const response = await api.post(`/admin/auctions/${auctionId}/approve`);
    return response.data;
  },

  /**
   * Reject a pending auction
   * @param {number} auctionId - Auction ID
   * @param {string} reason - Rejection reason
   */
  async rejectAuction(auctionId, reason) {
    const response = await api.post(`/admin/auctions/${auctionId}/reject`, { reason });
    return response.data;
  },

  /**
   * Force end an active auction
   * @param {number} auctionId - Auction ID
   */
  async endAuction(auctionId) {
    const response = await api.post(`/admin/auctions/${auctionId}/end`);
    return response.data;
  },

  /**
   * Cancel an auction (with refund processing)
   * @param {number} auctionId - Auction ID
   * @param {string} reason - Cancellation reason
   */
  async cancelAuction(auctionId, reason) {
    const response = await api.post(`/admin/auctions/${auctionId}/cancel`, { reason });
    return response.data;
  },

  // ===== DEPOSIT & BID MANAGEMENT (NEW) =====
  
  /**
   * Get all bidders for an auction with deposit status
   * @param {number} auctionId - Auction ID
   */
  async getAuctionBidders(auctionId) {
    const response = await api.get(`/bids/admin/auction/${auctionId}/bidders`);
    return response.data;
  },

  /**
   * Process expired deposits (mark as failed)
   */
  async processExpiredDeposits() {
    const response = await api.post('/bids/process-expired-deposits');
    return response.data;
  },

  /**
   * Get all deposit transactions
   * @param {Object} params - Filters (status, auction_id, user_id)
   */
  async getAllDeposits(params = {}) {
    const response = await api.get('/admin/deposits', { params });
    return response.data;
  },

  // ===== FINAL PAYMENT MANAGEMENT (NEW) =====
  
  /**
   * Get all final payments pending verification
   */
  async getPendingVerifications() {
    const response = await api.get('/payments/pending-verification');
    return response.data;
  },

  /**
   * Verify or reject a final payment
   * @param {number} transactionId - Transaction ID
   * @param {boolean} approved - true to approve, false to reject
   * @param {string} notes - Admin notes
   */
  async verifyFinalPayment(transactionId, approved, notes = '') {
    const response = await api.post(`/payments/verify/${transactionId}`, {
      verified: approved,
      notes
    });
    return response.data;
  },

  /**
   * Get all final payment transactions
   * @param {Object} params - Filters
   */
  async getAllFinalPayments(params = {}) {
    const response = await api.get('/admin/final-payments', { params });
    return response.data;
  },

  // ===== REFUND & FORFEITURE MANAGEMENT (NEW) =====
  
  /**
   * Process refund for a non-winning bidder
   * @param {number} auctionId - Auction ID
   * @param {number} userId - User ID to refund
   * @param {string} notes - Refund notes
   */
  async processRefund(auctionId, userId, notes = '') {
    const response = await api.post(`/payments/refund/${auctionId}/user/${userId}`, { notes });
    return response.data;
  },

  /**
   * Process deposit forfeiture for winner who didn't pay
   * @param {number} auctionId - Auction ID
   * @param {string} notes - Forfeiture notes
   */
  async processForfeiture(auctionId, notes = '') {
    const response = await api.post(`/payments/forfeit/${auctionId}`, { notes });
    return response.data;
  },

  /**
   * Get all refund transactions
   * @param {Object} params - Filters
   */
  async getAllRefunds(params = {}) {
    const response = await api.get('/admin/refunds', { params });
    return response.data;
  },

  // ===== TRANSACTION MANAGEMENT =====
  
  /**
   * Get all transactions with filters
   * @param {Object} params - Filters (type, status, date_range, user_id, auction_id)
   */
  async getAllTransactions(params = {}) {
    const response = await api.get('/admin/payments', { params });
    return response.data;
  },

  /**
   * Get transaction details with user/auction info
   * @param {number} transactionId - Transaction ID
   */
  async getTransactionDetails(transactionId) {
    const response = await api.get(`/admin/transactions/${transactionId}`);
    return response.data;
  },

  // ===== REPORTING & EXPORTS =====
  
  /**
   * Generate auction report
   * @param {Object} params - Report parameters
   */
  async generateAuctionReport(params = {}) {
    const response = await api.get('/admin/reports/auctions', { params });
    return response.data;
  },

  /**
   * Generate financial report
   * @param {Object} params - Report parameters (date_range, etc.)
   */
  async generateFinancialReport(params = {}) {
    const response = await api.get('/admin/reports/financial', { params });
    return response.data;
  },

  /**
   * Export transactions to CSV/Excel
   * @param {Object} params - Export filters
   */
  async exportTransactions(params = {}) {
    const response = await api.get('/admin/exports/transactions', { 
      params,
      responseType: 'blob' 
    });
    return response.data;
  },

  // ===== DEPRECATED METHODS (Keep for compatibility) =====
  
  /**
   * @deprecated Use processExpiredDeposits() instead
   */
  async processExpiredBids() {
    console.warn('processExpiredBids() is deprecated. Use processExpiredDeposits()');
    const response = await api.post('/bids/process-expired');
    return response.data;
  },

  /**
   * @deprecated Use processRefund() with auction/user IDs
   */
  async processPendingTransactions() {
    console.warn('processPendingTransactions() is deprecated');
    const response = await api.post('/payments/process-pending');
    return response.data;
  }
};

// ===== ADMIN HELPER FUNCTIONS =====

/**
 * Get transaction summary statistics
 * @param {Array} transactions - List of transactions
 * @returns {Object} Summary stats
 */
export const getTransactionSummary = (transactions) => {
  const summary = {
    totalDeposits: 0,
    totalFinalPayments: 0,
    totalRefunds: 0,
    totalForfeitures: 0,
    pendingVerification: 0,
    totalRevenue: 0,
    totalRefunded: 0
  };
  
  transactions?.forEach(t => {
    const amount = parseFloat(t.amount);
    
    switch(t.transaction_type) {
      case 'deposit':
        summary.totalDeposits += amount;
        if (t.status === 'completed') summary.totalRevenue += amount;
        break;
      case 'final_payment':
        summary.totalFinalPayments += amount;
        if (t.status === 'completed') summary.totalRevenue += amount;
        if (t.status === 'pending_verification') summary.pendingVerification++;
        break;
      case 'refund':
        summary.totalRefunds += amount;
        summary.totalRefunded += amount;
        break;
      case 'forfeiture':
        summary.totalForfeitures += amount;
        break;
    }
  });
  
  return summary;
};

/**
 * Get auction completion status
 * @param {Object} auction - Auction object
 * @returns {Object} Completion status info
 */
export const getAuctionCompletionStatus = (auction) => {
  const status = {
    hasWinner: !!auction.winner_id,
    hasDeposits: false,
    hasFinalPayment: false,
    allRefundsProcessed: false,
    readyForCompletion: false
  };
  
  // This would need transaction data to be accurate
  return status;
};

/**
 * Format admin action buttons based on transaction status
 * @param {Object} transaction - Transaction object
 * @returns {Array} Available actions
 */
export const getAvailableAdminActions = (transaction) => {
  const actions = [];
  
  if (!transaction) return actions;
  
  if (transaction.transaction_type === 'final_payment') {
    if (transaction.status === 'pending_verification') {
      actions.push(
        { label: 'Verify', action: 'verify', icon: '✅', color: 'green' },
        { label: 'Reject', action: 'reject', icon: '❌', color: 'red' }
      );
    }
  }
  
  if (transaction.transaction_type === 'deposit') {
    if (transaction.status === 'completed' && !transaction.refunded) {
      actions.push(
        { label: 'Refund', action: 'refund', icon: '↩️', color: 'yellow' }
      );
    }
  }
  
  return actions;
};